import { useCallback, useEffect } from 'react';

import { errorMessage } from '../../services/apiError';
import { chatService, ChatMessage, DEFAULT_CHAT_MODEL } from '../../services/chatService';
import type { SupervisorButton } from '../../types/supervisorButtons';
import type { QuotaQueueInfo } from '../../utils/quotaQueue';

import { CHAT_QUOTA_RETRIGGER_EVENT, dispatchChatQuotaQueued } from './ChatQuotaQueue';
import { dispatchSuggestedAgentsEvent } from './suggestionUtils';
import { ChatState, StreamingState, StreamingRefsFull, Ticker } from './types';
import { bindStreamingMessageId, handleStreamError, resetStreamingState } from './useChatBoxHelpers';

interface AIStreamProps {
  projectId: string | null;
  activeSessionId?: string | null;
  initialProjectBootstrap?: boolean;
  refsFull: StreamingRefsFull;
  ticker: Ticker;
  chatState: ChatState;
  streamingState: StreamingState;
  getOrderedTimestamp: () => string;
}

const createOnChunk = (refsFull: StreamingRefsFull, ticker: Ticker) => (c: string) => {
  refsFull.appendToBuffer(c);
  ticker.enqueueTokens(c);
};

const preserveActionMetadata = (
  message: ChatMessage,
  existing?: ChatMessage,
): ChatMessage => ({
  ...message,
  phase: message.phase ?? existing?.phase ?? null,
  allowed_buttons: message.allowed_buttons ?? existing?.allowed_buttons ?? null,
  buttons: message.buttons ?? existing?.buttons ?? null,
  clicked_button: message.clicked_button ?? existing?.clicked_button ?? null,
  clicked_buttons: message.clicked_buttons ?? existing?.clicked_buttons ?? null,
  tool_activity: message.tool_activity ?? existing?.tool_activity ?? null,
});

/** The reply's web searches as the saved message keeps them (none: null). */
const savedWebSearches = (streamingState: StreamingState) => {
  const searches = streamingState.getStreamingWebSearches();

  return searches.length ? [...searches] : null;
};

const createFinalizeCallback = (
  chatState: ChatState,
  projectId: string,
  getOrderedTimestamp: () => string,
  refsFull: StreamingRefsFull,
  streamingState: StreamingState,
  fallbackSessionId?: string | null,
) => (content: string, aiId: string, sessionId?: string | null) => {
  if (content.trim()) {
    const now = getOrderedTimestamp();

    chatState.setMessages((prev) => {
      const existing = prev.find((m) => m.id === aiId);
      const newMsg = {
        id: aiId, user_id: 'current-user', content, author: 'assistant',
        project_id: projectId, session_id: sessionId || fallbackSessionId || null,
        created_at: now, updated_at: now, tool_activity: savedWebSearches(streamingState),
      } as ChatMessage;

      return existing
        ? prev.map((m) => (m.id === aiId ? preserveActionMetadata(newMsg, m) : m))
        : [...prev, newMsg];
    });
  }
  resetStreamingState(refsFull, streamingState, chatState);
};

const createOnComplete = (
  refsFull: StreamingRefsFull,
  streamingState: StreamingState,
  finalizeCallback: (content: string, aiId: string, sessionId?: string | null) => void
) => ({ aiMessageId, sessionId }: { aiMessageId: string; sessionId?: string | null }) => {
  bindStreamingMessageId(refsFull, streamingState, aiMessageId);
  const content = refsFull.streamingBufferRef.current;
  const finalize = () => finalizeCallback(content, aiMessageId, sessionId);
  const isEmpty = refsFull.tokenQueueRef.current.length === 0 && !refsFull.tokenIntervalRef.current;

  if (isEmpty) finalize();
  else refsFull.setPendingCompletion({ finalizeCallback: finalize });
};

function prepareStreamingHelper(
  refsFull: StreamingRefsFull,
  ticker: Ticker,
  chatState: ChatState,
  streamingState: StreamingState
) {
  refsFull.abortStream();
  refsFull.setStreamingBuffer('');
  ticker.resetStreamingDisplay();
  chatState.setError(null);
  streamingState.setIsAiTyping(true);
}

const createOnSuggestedAgents = (
  refsFull: StreamingRefsFull,
  projectId: string,
  activeSessionId?: string | null,
) => (
  agents: string[],
  buttons: SupervisorButton[],
  pid: string,
  messageId: string,
  sessionId?: string | null,
  promptScope?: Record<string, unknown> | null,
  aiContent?: string | null,
) => {
  dispatchSuggestedAgentsEvent({
    agents,
    buttons,
    projectId: pid || projectId,
    messageId,
    sessionId: sessionId ?? activeSessionId ?? null,
    promptScope: promptScope ?? null,
    aiContent: aiContent ?? refsFull.streamingBufferRef.current,
  });
};

export const createStreamCallbacks = (
  refsFull: StreamingRefsFull,
  ticker: Ticker,
  chatState: ChatState,
  streamingState: StreamingState,
  projectId: string,
  finalizeCallback: (content: string, aiId: string, sessionId?: string | null) => void,
  activeSessionId?: string | null,
) => {
  const onError = (e: string) => {
    handleStreamError(
      e, chatState, streamingState, refsFull, ticker.resetStreamingDisplay, projectId
    );
  };

  // The message is saved; only its automatic reply waits for the plan's hourly quota.
  const onQuotaQueued = (info: QuotaQueueInfo) => {
    handleStreamError('', chatState, streamingState, refsFull, ticker.resetStreamingDisplay, projectId);
    chatState.setError(null);
    dispatchChatQuotaQueued({ info, content: '', projectId, sessionId: activeSessionId ?? null, kind: 'reply' });
  };

  const onStreamEnd = () => {
    refsFull.setStreamController(null);
    window.dispatchEvent(new Event('chat_sessions_changed'));
    window.dispatchEvent(new Event('chat_stream_end'));
    if (!refsFull.pendingCompletionRef.current && refsFull.tokenQueueRef.current.length === 0) {
      resetStreamingState(refsFull, streamingState);
    }
  };

  return {
    onAssistantMessageId: (messageId: string) => (
      bindStreamingMessageId(refsFull, streamingState, messageId)
    ),
    onChunk: createOnChunk(refsFull, ticker),
    onComplete: createOnComplete(refsFull, streamingState, finalizeCallback),
    onError,
    onQuotaQueued,
    onWebSearch: streamingState.upsertStreamingWebSearch,
    onSuggestedAgents: createOnSuggestedAgents(refsFull, projectId, activeSessionId),
    onStreamEnd
  };
};

/** A queued automatic reply is asked for again when ChatQuotaQueue releases it. */
const useQuotaRetrigger = (projectId: string | null | undefined, trigger: () => Promise<void>) => {
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as globalThis.CustomEvent<{ projectId?: string }>).detail;

      if (projectId && detail?.projectId === projectId) void trigger();
    };

    window.addEventListener(CHAT_QUOTA_RETRIGGER_EVENT, handler);

    return () => window.removeEventListener(CHAT_QUOTA_RETRIGGER_EVENT, handler);
  }, [projectId, trigger]);
};

export const useAIResponseStream = ({
  projectId, activeSessionId, initialProjectBootstrap,
  refsFull, ticker, chatState, streamingState, getOrderedTimestamp
}: AIStreamProps) => {

  const prepareStreaming = useCallback(() => {
    prepareStreamingHelper(refsFull, ticker, chatState, streamingState);
  }, [refsFull, ticker, chatState, streamingState]);

  const executeTriggerStream = useCallback(async () => {
    if (!projectId) return;

    const finalizeCallback = createFinalizeCallback(
      chatState, projectId, getOrderedTimestamp, refsFull, streamingState, activeSessionId
    );

    const callbacks = createStreamCallbacks(
      refsFull, ticker, chatState, streamingState, projectId, finalizeCallback, activeSessionId
    );

    const stream = await chatService.triggerAIResponseStream(
      projectId,
      DEFAULT_CHAT_MODEL,
      callbacks,
      activeSessionId,
      // Assign the controller when the request STARTS: handleEmptyQueue treats
      // a null controller as "stream finished" and would otherwise tear down
      // live streaming state on any >50ms token lull.
      (ctrl) => refsFull.setStreamController(ctrl),
      initialProjectBootstrap,
    );

    refsFull.setStreamController(stream);
  }, [
    activeSessionId,
    initialProjectBootstrap,
    projectId,
    refsFull,
    ticker,
    getOrderedTimestamp,
    chatState,
    streamingState,
  ]);

  const trigger = useCallback(async () => {
    prepareStreaming();
    try {
      await executeTriggerStream();
    } catch (error) {
      chatState.setError(errorMessage(
        error,
        'The AI response could not be started. Check your connection and try again.',
      ));
      streamingState.setIsAiTyping(false);
    }
  }, [prepareStreaming, executeTriggerStream, chatState, streamingState]);

  useQuotaRetrigger(projectId, trigger);

  return trigger;
};
